import { RequestStatus } from 'features/request/requestSlice';
import type { TUseRequestUpdaterProps } from 'features/request/useRequest';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    UerApplyMtrToWorkOrderChangeFromPriceListMutation,
    UerApplyMtrToWorkOrderFromPriceListMutation,
} from 'gql/graphql';
import { type WorkOrderChangeStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import {
    gqlApplyMtrToWorkOrderFromPriceList,
    uerApplyMtrToActKC2FromPriceListGql,
} from 'pages/Project/UerProject/WorkOrder/workOrderQueries';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { uerApplyMtrToWorkOrderChangeFromPriceListGql } from '../../WorkOrderChange/WorkOrderChangeQueries';

interface IUseApplyMtr {
    workOrderPriceId?: number;
    checkedMtrRowId?: number;
    workOrderStatus?: WorkOrderStatusEnum | WorkOrderChangeStatusEnum;
    refreshProject: () => void;
    onMatchApply?: () => any;
    isChange: boolean;
}

const requestId = 'applyMtr';

export const useApplyMtr = (props: IUseApplyMtr) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { workOrderPriceId, checkedMtrRowId, workOrderStatus, refreshProject, onMatchApply, isChange } = props;

    const [makeReset] = useResetStoreData({ requestId });

    const refreshAndCloseHandler = useCallback(() => {
        makeReset(undefined);
        refreshProject();

        if (onMatchApply) {
            onMatchApply();
        } else {
            navigate('../', { replace: true, state: { reload: Math.random() } });
        }
    }, [makeReset, navigate, onMatchApply, refreshProject]);

    const onApplyMtrToWorkOrderSuccess = useCallback(
        (depsVars: TUseRequestUpdaterProps<UerApplyMtrToWorkOrderFromPriceListMutation>) => {
            if (depsVars?.payload?.uerApplyMtrToWorkOrderFromPriceList?.result) {
                refreshAndCloseHandler();
            }
        },
        [refreshAndCloseHandler],
    );

    const [, applyMtrToWorkOrderStatus, , makeRequestApplyMtrToWorkOrder] = useRequest({
        requestId,
        query: gqlApplyMtrToWorkOrderFromPriceList,
        withEffect: false,
        dependenciesUpdater: onApplyMtrToWorkOrderSuccess,
    });

    const [, applyMtrToActKC2Status, , makeRequestApplyMtrToActKC2] = useRequest({
        requestId,
        query: uerApplyMtrToActKC2FromPriceListGql,
        withEffect: false,
        dependenciesUpdater: refreshAndCloseHandler,
    });

    const onApplyMtrToWorkOrderChangeSuccess = useCallback(
        (depsVars: TUseRequestUpdaterProps<UerApplyMtrToWorkOrderChangeFromPriceListMutation>) => {
            if (depsVars?.payload?.uerApplyMtrToWorkOrderChangeFromPriceList?.result) {
                refreshAndCloseHandler();
            }
        },
        [refreshAndCloseHandler],
    );

    const [, applyMtrToWorkOrderChangeStatus, , makeRequestApplyMtrToWorkOrderChange] = useRequest({
        requestId,
        query: uerApplyMtrToWorkOrderChangeFromPriceListGql,
        withEffect: false,
        dependenciesUpdater: onApplyMtrToWorkOrderChangeSuccess,
    });

    const isApplying = useMemo(
        () =>
            applyMtrToWorkOrderStatus === RequestStatus.FETCHING ||
            applyMtrToActKC2Status === RequestStatus.FETCHING ||
            applyMtrToWorkOrderChangeStatus === RequestStatus.FETCHING,
        [applyMtrToActKC2Status, applyMtrToWorkOrderChangeStatus, applyMtrToWorkOrderStatus],
    );

    const onModalConfirm = useCallback(() => {
        if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
            dispatch(makeRequestApplyMtrToActKC2({ workOrderPriceId, mtrRowId: checkedMtrRowId }));
        } else {
            if (isChange) {
                dispatch(
                    makeRequestApplyMtrToWorkOrderChange({
                        mtrRowId: checkedMtrRowId,
                        workOrderChangePriceId: workOrderPriceId,
                    }),
                );

                return;
            }

            dispatch(
                makeRequestApplyMtrToWorkOrder({
                    workOrderPriceId,
                    mtrRowId: checkedMtrRowId,
                }),
            );
        }
    }, [
        workOrderStatus,
        dispatch,
        makeRequestApplyMtrToActKC2,
        workOrderPriceId,
        checkedMtrRowId,
        isChange,
        makeRequestApplyMtrToWorkOrder,
        makeRequestApplyMtrToWorkOrderChange,
    ]);

    useEffect(() => {
        makeReset(undefined);
    }, [makeReset]);

    return {
        isApplying,
        onModalConfirm,
    };
};
